import { useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, Divider, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { transferOrdersApi } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { StatusPill, statusTone } from "@/components/StatusPill";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";

const STATUS_LABEL: Record<string, string> = {
  Draft: "Draft", Submitted: "Submitted", Approved: "Approved",
  Posted: "Dispatched", Cancelled: "Cancelled",
};

/**
 * UC-M2-TR-01 / UC-M2-TR-02 — Transfer detail with the full state machine actions.
 * Receive supports partial qty per line; the in-transit total turns the chip warning
 * until every line is fully received.
 */
export function TransferOrderDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const canCreate   = useAuth(s => s.hasPermission("warehouse.transfer.create"));
  const canApprove  = useAuth(s => s.hasPermission("warehouse.transfer.approve"));
  const canDispatch = useAuth(s => s.hasPermission("warehouse.transfer.dispatch"));
  const canReceive  = useAuth(s => s.hasPermission("warehouse.transfer.receive"));

  const [reason, setReason] = useState("");
  const [openAction, setOpenAction] = useState<null | "submit" | "approve" | "reject" | "cancel" | "dispatch" | "receive">(null);
  const [receiveLines, setReceiveLines] = useState<Record<string, number>>({});

  const query = useQuery({
    queryKey: ["transfer-order", id],
    queryFn: () => transferOrdersApi.get(id),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      if (!query.data) return;
      const rv = query.data.rowVersion;
      switch (openAction) {
        case "submit":   await transferOrdersApi.submit(id, rv); break;
        case "approve":  await transferOrdersApi.approve(id, rv); break;
        case "reject":   await transferOrdersApi.reject(id, reason, rv); break;
        case "cancel":   await transferOrdersApi.cancel(id, reason, rv); break;
        case "dispatch": await transferOrdersApi.dispatch(id, rv); break;
        case "receive":
          await transferOrdersApi.receive(id,
            Object.entries(receiveLines)
              .filter(([, q]) => q > 0)
              .map(([lineId, receivedQty]) => ({ lineId, receivedQty })),
            rv);
          break;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["transfer-order", id] });
      qc.invalidateQueries({ queryKey: ["transfer-orders"] });
      setOpenAction(null); setReason("");
    },
  });

  if (query.isLoading || !query.data) return <DetailSkeleton />;
  const t = query.data;

  const isDraft = t.status === "Draft";
  const isSubmitted = t.status === "Submitted";
  const isApproved = t.status === "Approved";
  const isDispatched = t.status === "Posted";
  const inTransit = t.lines.reduce((s, l) => s + l.inTransitQty, 0);

  const actionDisabled = mutation.isPending ||
    ((openAction === "reject" || openAction === "cancel") && !reason);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight={600}>{t.documentNo}</Typography>
          <StatusPill label={STATUS_LABEL[t.status] ?? t.status} tone={statusTone(t.status)} />
          {inTransit > 0 && <Chip label={`${inTransit} in transit`} color="warning" size="small" />}
        </Stack>
        <Stack direction="row" spacing={1}>
          {isDraft && canCreate && (
            <Button variant="contained" startIcon={<SendIcon />} onClick={() => setOpenAction("submit")}>Submit</Button>
          )}
          {isSubmitted && canApprove && (
            <>
              <Button variant="contained" color="success" startIcon={<CheckIcon />}
                onClick={() => setOpenAction("approve")}>Approve</Button>
              <Button color="warning" startIcon={<CancelIcon />}
                onClick={() => setOpenAction("reject")}>Reject</Button>
            </>
          )}
          {isApproved && canDispatch && (
            <Button variant="contained" color="primary" startIcon={<LocalShippingIcon />}
              onClick={() => setOpenAction("dispatch")}>Dispatch</Button>
          )}
          {isApproved && canCreate && (
            <Button color="error" startIcon={<CancelIcon />}
              onClick={() => setOpenAction("cancel")}>Cancel</Button>
          )}
          {isDispatched && canReceive && inTransit > 0 && (
            <Button variant="contained" color="success" startIcon={<MoveToInboxIcon />}
              onClick={() => {
                setReceiveLines(Object.fromEntries(t.lines.map(l => [l.id, l.pendingReceipt])));
                setOpenAction("receive");
              }}>Receive at destination</Button>
          )}
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Stack>
            <Typography variant="caption" color="text.secondary">From warehouse</Typography>
            <Typography>{t.sourceWarehouseName}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">To warehouse</Typography>
            <Typography>{t.destWarehouseName}</Typography>
          </Stack>
          <Stack>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography>{formatDate(t.documentDate)}</Typography>
          </Stack>
          {t.expectedDeliveryDate && (
            <Stack>
              <Typography variant="caption" color="text.secondary">Expected delivery</Typography>
              <Typography>{formatDate(t.expectedDeliveryDate)}</Typography>
            </Stack>
          )}
          {t.transferRequisitionId && (
            <Stack>
              <Typography variant="caption" color="text.secondary">From requisition</Typography>
              <Typography sx={{ fontFamily: "monospace" }}>{t.transferRequisitionId.slice(0, 8)}…</Typography>
            </Stack>
          )}
        </Stack>
        {t.notes && (
          <Stack sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Notes</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{t.notes}</Typography>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell align="right">Requested</TableCell>
              <TableCell align="right">Dispatched</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">In transit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {t.lines.map(l => (
              <TableRow key={l.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                </TableCell>
                <TableCell>{l.batchNo ?? "—"}</TableCell>
                <TableCell align="right">{l.requestedQty}</TableCell>
                <TableCell align="right">{l.dispatchedQty}</TableCell>
                <TableCell align="right">{l.receivedQty}</TableCell>
                <TableCell align="right">
                  {l.inTransitQty > 0
                    ? <Chip size="small" color="warning" label={l.inTransitQty} />
                    : l.inTransitQty}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Button onClick={() => navigate("/warehouse/transfers")}>Back to list</Button>

      <Dialog open={openAction !== null} onClose={() => { setOpenAction(null); setReason(""); }} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => { setOpenAction(null); setReason(""); }}>
          {openAction === "submit" && "Submit transfer"}
          {openAction === "approve" && "Approve transfer"}
          {openAction === "reject" && "Reject transfer"}
          {openAction === "cancel" && "Cancel transfer"}
          {openAction === "dispatch" && "Dispatch transfer"}
          {openAction === "receive" && "Receive at destination"}
        </ClosableDialogTitle>
        <DialogContent>
          {(openAction === "submit" || openAction === "approve" || openAction === "dispatch") && (
            <DialogContentText>
              {openAction === "dispatch"
                ? "Dispatching writes TRANSFER_OUT + IN_TRANSIT_OUT at the source warehouse. The stock then sits in transit until received."
                : "Confirm the state transition."}
            </DialogContentText>
          )}
          {(openAction === "reject" || openAction === "cancel") && (
            <TextField label="Reason *" fullWidth multiline minRows={3} sx={{ mt: 1 }}
              value={reason} onChange={(e) => setReason(e.target.value)} />
          )}
          {openAction === "receive" && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Partial receive supported. Editing each line confirms how many units arrived at the destination.
              </DialogContentText>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="right">Receiving now</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {t.lines.filter(l => l.pendingReceipt > 0).map(l => (
                    <TableRow key={l.id}>
                      <TableCell>{l.itemName} <Typography variant="caption" color="text.secondary">{l.sku}</Typography></TableCell>
                      <TableCell align="right">{l.pendingReceipt}</TableCell>
                      <TableCell align="right">
                        <TextField type="number" size="small" sx={{ width: 100 }}
                          value={receiveLines[l.id] ?? l.pendingReceipt}
                          inputProps={{ min: 0, max: l.pendingReceipt, step: "0.001", style: { textAlign: "right" } }}
                          onChange={(e) => setReceiveLines(prev => ({ ...prev, [l.id]: Number(e.target.value) || 0 }))} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
          {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(mutation.error)}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAction(null); setReason(""); }}>Cancel</Button>
          <Button variant="contained" disabled={actionDisabled}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Divider sx={{ my: 2 }} />
    </Box>
  );
}
